/**
 * Import files into the cloud library and optionally into the current scene.
 *
 * This is the single source of truth for mesh-file and Blend imports.
 * All import paths (Add Object panel, drag-and-drop, keyboard shortcut,
 * Library page) funnel through these functions.
 *
 * Mesh-file and Blend imports are deduplicated by source file hash: if an item with
 * the same SHA-256 already exists in the user's library, the existing item is
 * returned without creating a duplicate.
 *
 * Public API:
 * - importObjAndSpawn(file)       — import OBJ to library + spawn into scene
 * - importGlbAndSpawn(file)       — import GLB/glTF to library + spawn into scene
 * - importFbxAndSpawn(file)       — import FBX to library + spawn into scene
 * - importBlendAndLoad(file,mode) — import Blend to library + load/add to scene
 * - importObjToLibrary(file)      — import OBJ to library only
 * - importGlbToLibrary(file)      — import GLB/glTF to library only
 * - importFbxToLibrary(file)      — import FBX to library only
 * - importBlendToLibrary(file,opts?) — import Blend to library only
 * - importPrToLibrary(file)       — import .pr to library only
 */

import type { LibraryItemResponse, LibraryItemType } from "@poseref/shared";
import { uploadToPresignedUrl } from "../api/client.ts";
import { fetchItem } from "../api/library.ts";
import {
  checkQuota,
  findItemByHash,
  getPresignedUploadUrl,
} from "../api/upload.ts";
import type { ImportedMeshResult } from "../modeling/importTypes.ts";
import type { LoopNormals, RenderTriangleIndices } from "../modeling/types.ts";
import { useAuthStore } from "./authStore.ts";
import { computeFileHash } from "./fileHash.ts";
import { useLibraryStore } from "./libraryStore.ts";
import { buildImportedMeshLibraryBundle } from "./meshFileImport.ts";
import {
  type MeshSourceFormat,
  meshSourceFormatFromFileName,
  stripMeshImportExtension,
} from "./meshImportFormats.ts";
import { spawnFromCloudLibrary } from "./spawnFromLibrary.ts";
import { useStore } from "./store.ts";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function generateKeys(type: LibraryItemType, ext = ".pr") {
  const timestamp = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  const fileKey = `library/${type}s/${timestamp}_${rand}${ext}`;
  const thumbnailKey = `library/thumbnails/${timestamp}_${rand}.webp`;
  return { fileKey, thumbnailKey };
}

async function uploadThumbnail(
  meshes: {
    vertices: Float32Array;
    indices: RenderTriangleIndices;
    loopNormals: LoopNormals;
    topology?: import("../modeling/types.ts").PolygonTopology;
    uvs?: Float32Array;
  }[],
  thumbnailKey: string,
): Promise<string | undefined> {
  try {
    const { renderThumbnail } = await import("./offscreenThumbnail.ts");
    const blob = await renderThumbnail(meshes);
    if (blob) {
      const presign = await getPresignedUploadUrl(thumbnailKey, "image/webp");
      await uploadToPresignedUrl(presign.url, blob);
      return thumbnailKey;
    }
  } catch (e) {
    console.warn("Thumbnail generation failed, continuing without:", e);
  }
  return undefined;
}

/**
 * Upload a source file to R2, deduplicating by content hash.
 */
async function uploadSourceFile(
  buffer: ArrayBuffer,
  fileName: string,
  hash: string,
  prefix: string,
): Promise<string> {
  const { checkFileHash } = await import("../api/upload.ts");
  const existing = await checkFileHash(hash);
  if (existing.exists && existing.key) {
    return existing.key;
  }
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
  const key = `${prefix}/${hash.slice(0, 12)}_${sanitized}`;
  const presign = await getPresignedUploadUrl(key, "application/octet-stream");
  await uploadToPresignedUrl(
    presign.url,
    new Blob([buffer], { type: "application/octet-stream" }),
  );
  return key;
}

/**
 * Check if a library item with this source hash already exists.
 * If so, refresh the store and return it.
 */
async function findExistingByHash(
  hash: string,
): Promise<LibraryItemResponse | null> {
  const result = await findItemByHash(hash);
  if (!result.exists || !result.itemId) return null;

  const item = await fetchItem(result.itemId);
  const store = useLibraryStore.getState();
  if (!store.items.some((i) => i.id === item.id)) {
    useLibraryStore.setState((s) => ({ items: [item, ...s.items] }));
  }
  return item;
}

// ---------------------------------------------------------------------------
// Shared mesh-file helpers
// ---------------------------------------------------------------------------

type MeshImportParser = (
  buffer: ArrayBuffer,
) => Promise<ImportedMeshResult[]> | ImportedMeshResult[];

async function importMeshToLibrary(
  file: File,
  sourceFormat: MeshSourceFormat,
  parse: MeshImportParser,
): Promise<LibraryItemResponse> {
  const buffer = await file.arrayBuffer();
  const hash = await computeFileHash(buffer);

  const existing = await findExistingByHash(hash);
  if (existing) return existing;

  await checkQuota(buffer.byteLength);

  const results = await parse(buffer);
  if (results.length === 0) {
    throw new Error(`No geometry found in ${sourceFormat.toUpperCase()} file`);
  }

  const baseName = stripMeshImportExtension(file.name);
  const bundle = buildImportedMeshLibraryBundle(
    baseName,
    sourceFormat,
    results,
  );

  const fileBlob = new Blob([JSON.stringify(bundle.fileData)], {
    type: "application/json",
  });

  const { fileKey, thumbnailKey } = generateKeys("collection");

  const [, sourceFileKey, uploadedThumbKey] = await Promise.all([
    getPresignedUploadUrl(fileKey, "application/json").then((p) =>
      uploadToPresignedUrl(p.url, fileBlob),
    ),
    uploadSourceFile(buffer, file.name, hash, sourceFormat),
    uploadThumbnail(bundle.sceneObjects, thumbnailKey),
  ]);

  const item = await useLibraryStore.getState().createItem({
    name: bundle.itemName,
    type: "collection",
    fileKey,
    thumbnailKey: uploadedThumbKey,
    sourceFileKey,
    sourceHash: hash,
    fileSize: buffer.byteLength,
    metadata: bundle.metadata,
  });
  useAuthStore.getState().refreshStorageUsage();
  return item;
}

async function loadMeshImportParser(
  sourceFormat: MeshSourceFormat,
): Promise<MeshImportParser> {
  switch (sourceFormat) {
    case "obj": {
      const { parseObj } = await import("../modeling/objImporter.ts");
      return (buffer) => parseObj(new TextDecoder().decode(buffer));
    }
    case "glb":
    case "gltf": {
      const { parseGlb } = await import("../modeling/glbImporter.ts");
      return parseGlb;
    }
    case "fbx": {
      const { parseFbx } = await import("../modeling/fbxImporter.ts");
      return parseFbx;
    }
  }
}

async function importLibraryItemAndSpawn(
  importItem: () => Promise<LibraryItemResponse>,
): Promise<string[]> {
  const store = useStore.getState();
  store.setLoading(true);
  store.setError(null);
  try {
    const item = await importItem();
    const ids = await spawnFromCloudLibrary(item);
    useStore.setState({ loading: false });
    return ids;
  } catch (e) {
    useStore.setState({
      error: e instanceof Error ? e.message : String(e),
      loading: false,
    });
    return [];
  }
}

export async function importMeshFileToLibrary(
  file: File,
): Promise<LibraryItemResponse> {
  const sourceFormat = meshSourceFormatFromFileName(file.name);
  const parse = await loadMeshImportParser(sourceFormat);
  return importMeshToLibrary(file, sourceFormat, parse);
}

export async function importMeshFileAndSpawn(file: File): Promise<string[]> {
  return importLibraryItemAndSpawn(() => importMeshFileToLibrary(file));
}

// ---------------------------------------------------------------------------
// OBJ → library
// ---------------------------------------------------------------------------

export async function importObjToLibrary(
  file: File,
): Promise<LibraryItemResponse> {
  const { parseObj } = await import("../modeling/objImporter.ts");
  return importMeshToLibrary(file, "obj", (buffer) =>
    parseObj(new TextDecoder().decode(buffer)),
  );
}

// ---------------------------------------------------------------------------
// OBJ → library + scene (unified entry point for all OBJ imports)
// ---------------------------------------------------------------------------

/**
 * Import an OBJ file to the library (deduplicated) and spawn it into the
 * current scene. This is the single entry point for all OBJ imports.
 */
export async function importObjAndSpawn(file: File): Promise<string[]> {
  return importLibraryItemAndSpawn(() => importObjToLibrary(file));
}

// ---------------------------------------------------------------------------
// GLB/glTF → library
// ---------------------------------------------------------------------------

export async function importGlbToLibrary(
  file: File,
): Promise<LibraryItemResponse> {
  const { parseGlb } = await import("../modeling/glbImporter.ts");
  return importMeshToLibrary(
    file,
    meshSourceFormatFromFileName(file.name),
    parseGlb,
  );
}

// ---------------------------------------------------------------------------
// GLB/glTF → library + scene (unified entry point for all GLB imports)
// ---------------------------------------------------------------------------

export async function importGlbAndSpawn(file: File): Promise<string[]> {
  return importLibraryItemAndSpawn(() => importGlbToLibrary(file));
}

// ---------------------------------------------------------------------------
// FBX → library
// ---------------------------------------------------------------------------

export async function importFbxToLibrary(
  file: File,
): Promise<LibraryItemResponse> {
  const { parseFbx } = await import("../modeling/fbxImporter.ts");
  return importMeshToLibrary(file, "fbx", parseFbx);
}

// ---------------------------------------------------------------------------
// FBX → library + scene (unified entry point for all FBX imports)
// ---------------------------------------------------------------------------

export async function importFbxAndSpawn(file: File): Promise<string[]> {
  return importLibraryItemAndSpawn(() => importFbxToLibrary(file));
}

// ---------------------------------------------------------------------------
// Blend → library (as object/collection)
// ---------------------------------------------------------------------------

/** Pre-parsed data to avoid redundant file reads and parsing. */
interface BlendImportOpts {
  buffer: ArrayBuffer;
  hash: string;
}

/**
 * Import a Blend file to the library.
 *
 * Stores the raw .blend binary as the primary file — no lossy conversion.
 * On spawn, `spawnFromCloudLibrary` downloads the .blend and feeds it
 * through `addBlendFileParsed`, preserving materials, shape keys, hierarchy,
 * visibility, modifiers, and drivers at full fidelity.
 */
export async function importBlendToLibrary(
  file: File,
  opts?: BlendImportOpts,
): Promise<LibraryItemResponse> {
  const { parseBlendFileAsync } = await import(
    "../blend-reader/parseBlendFileAsync.ts"
  );
  const { ObjectType } = await import("../blend-reader/types/BlendObject.ts");

  // Use pre-computed buffer/hash if available, otherwise read from file
  const buffer = opts?.buffer ?? (await file.arrayBuffer());
  const hash = opts?.hash ?? (await computeFileHash(buffer));

  const existing = await findExistingByHash(hash);
  if (existing) return existing;

  // Check storage quota before uploading (throws + shows modal on denial)
  await checkQuota(buffer.byteLength);

  // Parse only for metadata + thumbnail — the raw .blend is the stored file.
  const blendFile = await parseBlendFileAsync(buffer.slice(0));

  const baseName = file.name.replace(/\.blend$/i, "");
  const meshObjects = blendFile.objects.filter(
    (obj) => obj.type === ObjectType.Mesh && obj.meshRef,
  );
  const objectNames = meshObjects.map((o) => o.name);
  if (meshObjects.length === 0) {
    throw new Error("No mesh objects found in blend file");
  }

  const name =
    meshObjects.length === 1
      ? meshObjects[0].name
      : `${baseName} (${meshObjects.length} objects)`;

  // Upload the raw .blend as the primary file
  const { fileKey, thumbnailKey } = generateKeys("blend", ".blend");
  const fileBlob = new Blob([buffer], { type: "application/octet-stream" });

  // Build thumbnail meshes from the parse
  const thumbMeshes: {
    vertices: Float32Array;
    indices: RenderTriangleIndices;
    loopNormals: LoopNormals;
    uvs?: Float32Array;
  }[] = [];
  for (const obj of meshObjects) {
    const mesh = blendFile.meshes.get(obj.meshRef!);
    if (mesh && mesh.positions.length > 0) {
      thumbMeshes.push({
        vertices: mesh.positions,
        indices: mesh.indices,
        loopNormals: mesh.loopNormals,
        uvs: mesh.uvs,
      });
    }
  }

  const [, uploadedThumbKey] = await Promise.all([
    getPresignedUploadUrl(fileKey, "application/octet-stream").then((p) =>
      uploadToPresignedUrl(p.url, fileBlob),
    ),
    uploadThumbnail(thumbMeshes, thumbnailKey),
  ]);

  let vertexCount = 0;
  for (const m of thumbMeshes) vertexCount += m.vertices.length / 3;

  const item = await useLibraryStore.getState().createItem({
    name,
    type: "blend",
    fileKey,
    thumbnailKey: uploadedThumbKey,
    sourceHash: hash,
    fileSize: buffer.byteLength,
    metadata: {
      vertexCount,
      objectCount: meshObjects.length,
      objectNames,
    },
  });
  useAuthStore.getState().refreshStorageUsage();
  return item;
}

// ---------------------------------------------------------------------------
// Blend → library + scene (unified entry point for all Blend import paths)
// ---------------------------------------------------------------------------

/**
 * Load or merge a Blend file into the current scene.
 *
 * - "load": Replace the current blend scene (drag-and-drop / Ctrl+O).
 *           The blend is persisted to the cloud when the user saves the scene.
 * - "add":  Import to the library (deduplicated) and merge into the current
 *           scene.  The blend file becomes a reusable library item *and* its
 *           objects are added to the viewport in a single operation — mirroring
 *           how mesh-file imports work via `importMeshFileAndSpawn`.
 */
export async function importBlendAndLoad(
  file: File,
  mode: "load" | "add",
): Promise<void> {
  const store = useStore.getState();
  store.setLoading(true);
  store.setError(null);

  try {
    const buffer = await file.arrayBuffer();
    const bufferCopy = buffer.slice(0);
    const hash = await computeFileHash(bufferCopy);

    if (mode === "load") {
      const { parseBlendFileAsync } = await import(
        "../blend-reader/parseBlendFileAsync.ts"
      );
      const blendFile = await parseBlendFileAsync(buffer);
      await store.loadFile(blendFile, file.name, bufferCopy);
    } else {
      // Switch to object mode if not already there
      if (store.workspaceMode !== "object") {
        store.setWorkspaceMode("object");
      }

      // Import to library first (deduplicated by content hash) so the blend
      // file appears in the user's library and can be reused in other scenes.
      await importBlendToLibrary(file, { buffer: bufferCopy, hash });

      // Then merge into the current scene.
      await store.addBlendFileParsed(bufferCopy, hash, file.name);
    }
  } catch (e) {
    useStore.setState({
      error: e instanceof Error ? e.message : String(e),
      loading: false,
    });
  }
}

// ---------------------------------------------------------------------------
// .pr → library
// ---------------------------------------------------------------------------

export async function importPrToLibrary(
  file: File,
): Promise<LibraryItemResponse> {
  const { parseLibraryFile, decodeLibraryObject } = await import(
    "./libraryIO.ts"
  );

  // Check storage quota before uploading (throws + shows modal on denial)
  await checkQuota(file.size);

  const text = await file.text();
  const parsed = parseLibraryFile(text);

  const type: LibraryItemType =
    parsed.kind === "object"
      ? "object"
      : parsed.kind === "collection"
        ? "collection"
        : "scene";

  const { fileKey, thumbnailKey } = generateKeys(type);

  // Upload the .pr file as-is — no re-encode pass.
  const fileBlob = new Blob([text], { type: "application/json" });
  const uploadPromise = getPresignedUploadUrl(fileKey, "application/json").then(
    (p) => uploadToPresignedUrl(p.url, fileBlob),
  );

  // Render a thumbnail for object/collection types.
  let uploadedThumbKey: string | undefined;
  if (parsed.kind === "object") {
    const obj = decodeLibraryObject(parsed.object);
    const [, key] = await Promise.all([
      uploadPromise,
      uploadThumbnail([obj], thumbnailKey),
    ]);
    uploadedThumbKey = key;
  } else if (parsed.kind === "collection") {
    const objs = parsed.objects.map((s) => decodeLibraryObject(s));
    const [, key] = await Promise.all([
      uploadPromise,
      uploadThumbnail(objs, thumbnailKey),
    ]);
    uploadedThumbKey = key;
  } else {
    await uploadPromise;
  }

  const name = file.name.replace(/\.pr$/i, "");

  const item = await useLibraryStore.getState().createItem({
    name,
    type,
    fileKey,
    thumbnailKey: uploadedThumbKey,
    fileSize: file.size,
  });
  useAuthStore.getState().refreshStorageUsage();
  return item;
}
